import { Injectable, inject } from '@angular/core';
import { Logger } from '@nsalaun/ng-logger';
import { OAuthEvent } from 'angular-oauth2-oidc';
import { EMPTY, Observable, Subject, merge } from 'rxjs';
import { catchError, exhaustMap, filter, mergeMap, skip, take, tap, throttleTime } from 'rxjs/operators';

import { FreezeResumeService } from '@mysvg/utils/services/freeze-resume.service';
import { NetworkConnectionService } from '@mysvg/utils/services/network-connection.service';

import { Context } from '../../context/models/context.model';
import { DefaultUserTokenClaims } from '../../context/models/user-token-claims.model';
import { ContextService } from '../../context/services/context.service';
import { OAUTH_EVENTS } from '../configs/config';
import { BrowserPageVisibilityApiService } from './browser-page-visibility-api.service';
import { OauthWrapperService } from './oauth-wrapper.service';

const SILENT_REFRESH_IFRAME_NAME = 'angular-oauth-oidc-silent-refresh-iframe';

// The AuthRefreshService tries to refresh authentication after inactive tab comes back to the foreground, since subscriptions do not run if
// the browser tab is inactive (behaviour is browser dependant). If the tab is active the silent refresh will be done automatically by
// `oauth-oidc` for us. The `triggerRefresh` method can also be called in error interceptors after receiving a 401 status code.
@Injectable({ providedIn: 'root' })
export class AuthRefreshService<T extends Context, U extends DefaultUserTokenClaims> {
	private contextService = inject<ContextService<T, U>>(ContextService);
	private freezeResumeService = inject(FreezeResumeService);
	private logger = inject(Logger);
	private networkConnectionService = inject(NetworkConnectionService);
	private oauthWrapperService = inject<OauthWrapperService<U>>(OauthWrapperService);
	private pageVisibilityService = inject(BrowserPageVisibilityApiService);

	private refreshTrigger$ = new Subject<void>();
	private isOnlineButNotLoggedInTrigger$: Observable<boolean>;

	init(): void {
		this.isOnlineButNotLoggedInTrigger$ = this.getOnlineButNoTokenTrigger();
		this.initGeneralRefreshLogic();

		merge(this.isOnlineButNotLoggedInTrigger$, this.refreshTrigger$)
			.pipe(
				// after one event triggered, all others are ignored for 2000 ms
				throttleTime(2000),
				// while refresh runs, other events are ignored (throttleTime and exhaustMap are double-checking only one event triggers refresh)
				exhaustMap(() =>
					this.oauthWrapperService.manualRefresh().pipe(
						catchError((error: Error) => {
							this.logger.debug('[OAuthWrapper] says manual silent refresh failed with ' + JSON.stringify(error) + ', logging out user');
							this.contextService.logout();
							return EMPTY;
						}),
					),
				),
			)
			.subscribe(
				() => this.logger.debug('[OAuthWrapper] says manual refresh done.'),
				// this error should never occur, cause the source observable only emits events, and the mergeMap catches potential errors
				(error: Error) => {
					this.logger.debug('[OAuthWrapper] says manual silent refresh failed with unexpected error ' + JSON.stringify(error));
				},
			);

		// if not frozen then its resume
		const isResume$ = this.freezeResumeService.isFrozen().pipe(filter((isFrozen: boolean) => !isFrozen));

		/**
		 * [NOTE] inactive tab / browser does not refresh token via javascript sometimes, which results in 401 responses
		 *				avoid this using manual refresh
		 */
		merge(this.pageVisibilityService.getReEnterState(), isResume$).subscribe(() => {
			this.logger.debug('[AuthRefreshService] says reentered page.');
			if (!this.oauthWrapperService.hasValidAccessTokenWithExpirationCheck() && this.networkConnectionService.isOnline()) {
				this.logger.debug('[AuthRefreshService] says reentered page and no valid access token found.');
				this.triggerRefresh();
			}
		});
	}

	triggerRefresh(): void {
		this.logger.debug('[AuthRefreshService] says triggerRefresh, for manual silent refresh.');
		this.refreshTrigger$.next();
	}

	/**
	 * setup silent refresh and listen to refresh events
	 */
	private initGeneralRefreshLogic(): void {
		this.afterUserLoggedIn()
			.pipe(
				take(1),
				tap(() => this.oauthWrapperService.setupAutomaticSilentRefresh()),
				mergeMap(() => this.oauthWrapperService.getSilentRefreshEvents()),
			)
			.subscribe((event: OAuthEvent) => this.handleRefreshEvents(event.type));
	}

	private afterUserLoggedIn(): Observable<T> {
		return this.contextService.getFirstContext().pipe(filter((context: T) => !context.isNotLoggedInState()));
	}

	private handleRefreshEvents(eventName: string): void {
		this.logger.debug('[AuthRefreshService] says handleRefreshEvents, with event: ' + eventName);
		switch (eventName) {
			case OAUTH_EVENTS.TOKEN_REFRESHED: {
				this.logger.debug('[AuthRefreshService] says OAUTH_EVENTS.TOKEN_REFRESHED occurred, do initCustomLoginFlow');
				this.contextService.initCustomLoginFlow();
				break;
			}
			case OAUTH_EVENTS.SILENT_REFRESH.REFRESHED: {
				this.logger.debug('[AuthRefreshService] says OAUTH_EVENTS.SILENT_REFRESH.REFRESHED occurred, do initCustomLoginFlow');
				this.removeSilentRefreshIframe();
				this.contextService.initCustomLoginFlow();
				break;
			}
			case OAUTH_EVENTS.SILENT_REFRESH.TIMEOUT: {
				this.logger.debug(
					'[AuthRefreshService] says OAUTH_EVENTS.SILENT_REFRESH.TIMEOUT occurred, if online logout user, this is totally unexpected, if offline this is expected to fail.',
				);
				if (this.networkConnectionService.isOnline()) {
					this.logger.debug(
						'[AuthRefreshService] says handleRefreshTimeout ran into refresh timout, but because user is online, lets logout',
					);
					this.contextService.logout();
				}
				break;
			}
			case OAUTH_EVENTS.SILENT_REFRESH.ERROR: {
				this.logger.debug('[AuthRefreshService] says OAUTH_EVENTS.SILENT_REFRESH.ERROR occurred and contextService calls logout');
				return this.contextService.logout();
			}
			default:
				this.logger.debug('[AuthRefreshService] says unknown and unexpected oauth event triggered, call logout');
				return this.contextService.logout();
		}
	}

	/**
	 * TODO - this should not be necessary
	 */
	private removeSilentRefreshIframe(): void {
		const iframe$ = document.getElementById(SILENT_REFRESH_IFRAME_NAME);
		if (!!iframe$) {
			document.body.removeChild(iframe$);
		}
	}

	/**
	 * create observable that fires, when online (not initially) triggered and no valid token is found
	 */
	private getOnlineButNoTokenTrigger(): Observable<boolean> {
		return this.networkConnectionService.isOnline$().pipe(
			skip(1), // initial value should be skipped
			tap((isOnline: boolean) => this.logger.debug('[AuthRefreshService] says network changed to isOnline: ' + isOnline)),
			filter((isOnline: boolean) => isOnline && !this.oauthWrapperService.hasValidAccessTokenWithExpirationCheck()),
			tap(() => this.logger.debug('[AuthRefreshService] says status changed to isOnline but no valid access token found')),
		);
	}
}
